<template>
  <div ref="mapContainer" class="map-container"></div>
</template>

<script>
  import { Loader } from '@googlemaps/js-api-loader';

  export default {
    name: 'GoogleMap',
    data() {
      return {
        map: null,
        google: null,
        markerLibrary: null,
        markerInstances: [], // マーカーのインスタンスを管理
        bounceMarker: {
          id: '',
          content: null,
        }, // 現在bounce中のマーカー
      };
    },
    async mounted() {
      await this.initMap();
    },
    methods: {
      // Google Map を初期化する関数
      async initMap() {
        const loader = new Loader({
          apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
          version: 'weekly',
          libraries: ['marker', 'places'],
        });

        this.google = await loader.importLibrary('maps');
        this.markerLibrary = await loader.importLibrary('marker');
        const mapContainer = this.$refs.mapContainer;

        // 初期表示は東京を中心座標に設定
        const initialCenter = { lat: 35.6895, lng: 139.6917 };

        this.map = new this.google.Map(mapContainer, {
          center: initialCenter,
          zoom: 10,
          mapId: process.env.VUE_APP_GOOGLE_MAP_ID,
          language: 'ja',
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        });
      },
      // 特定のマーカーを更新する関数
      updateMarker(markerData) {
        const marker = this.markerInstances.find((m) => m.id === markerData.id);
        if (!marker) {
          // フィルター等でマーカーが見つからない場合は何もしない
          return;
        }

        // PinElementを更新
        const { PinElement } = this.markerLibrary;
        const newPin = new PinElement({
          background: markerData.background,
          borderColor: markerData.borderColor,
          glyphColor: markerData.glyphColor,
          glyph: markerData.glyph,
          scale: markerData.scale,
        });

        marker.content = newPin.element;

        if (marker.id === this.bounceMarker.id) {
          // 既にbounceしているマーカーの場合、再度bounceを設定
          this.setBounceMarker(marker.id, marker.content);
        }

        // マーカーの位置を地図に反映
        marker.setMap(this.map);
      },
      // マーカーを更新する関数
      updateMarkers(newMarkers) {
        if (!this.markerLibrary || !this.markerLibrary.AdvancedMarkerElement) {
          console.error('AdvancedMarkerElement が利用できません');
          return;
        }
        const { AdvancedMarkerElement, PinElement } = this.markerLibrary;
        // id をキーにした既存マーカーのマップを作成
        const markerMap = new Map(this.markerInstances.map((m) => [m.id, m]));
        // 更新後のマーカーリストを作成
        const updatedMarkers = newMarkers.map((markerData) => {
          const existingMarker = markerMap.get(markerData.id);
          if (existingMarker) {
            // 既存のマーカーがある場合、位置を更新
            existingMarker.position = markerData.position;

            // PinElementを新しく作成し、contentを更新
            const newPin = new PinElement({
              background: markerData.background,
              borderColor: markerData.borderColor,
              glyphColor: markerData.glyphColor,
              glyph: markerData.glyph,
              scale: markerData.scale,
            });

            existingMarker.content = newPin.element;
            if (existingMarker.id === this.bounceMarker.id) {
              // 既にbounceしているマーカーの場合、再度bounceを設定
              this.setBounceMarker(existingMarker.id, existingMarker.content);
            }

            markerMap.delete(markerData.id); // 削除対象から除外
            return existingMarker;
          }

          // 新しいマーカーを作成
          const pin = new PinElement({
            background: markerData.background,
            borderColor: markerData.borderColor,
            glyphColor: markerData.glyphColor,
            glyph: markerData.glyph,
            scale: markerData.scale,
          });

          const marker = new AdvancedMarkerElement({
            map: this.map,
            position: markerData.position,
            title: markerData.title,
            content: pin.element,
            gmpClickable: true,
          });

          marker.id = markerData.id; // マーカーにidを設定（識別用）

          marker.addEventListener('mouseenter', () => {
            pin.element.style.cursor = 'pointer';
          });

          marker.addEventListener('mouseleave', () => {
            pin.element.style.cursor = 'default';
          });

          marker.addListener('gmp-click', () => {
            this.setBounceMarker(marker.id, marker.content);
            this.setCenter(marker.position);
            this.$emit('set-target-marker', markerData.id);
          });

          if (marker.id === this.bounceMarker.id) {
            // 既にbounceしているマーカーの場合、再度bounceを設定
            this.setBounceMarker(marker.id, marker.content);
          }

          return marker;
        });

        // 残った古いマーカーを削除
        for (const oldMarker of markerMap.values()) {
          oldMarker.setMap(null);
        }

        //  更新後のマーカーリストをセット
        this.markerInstances = updatedMarkers;
      },
      // 中心座乗を変更する関数
      setCenter(newCenter) {
        if (this.map) {
          this.map.setCenter(newCenter);
        }
      },
      // マーカーをbounceさせる関数
      setBounceMarker(markerId, markerContent) {
        if (this.bounceMarker.id) {
          // 既存のbounce中のマーカーを解除
          this.removeBounceMarker();
        }

        markerContent.classList.add('bounce');
        this.bounceMarker = {
          id: markerId,
          content: markerContent,
        };
      },

      // 点滅を解除（親コンポーネントから呼び出し）
      removeBounceMarker() {
        if (this.bounceMarker.id) {
          this.bounceMarker.content.classList.remove('bounce');
          this.bounceMarker = {
            id: '',
            content: null,
          };
        }
      },
    },
  };
</script>
<style scoped>
  .map-container {
    width: 100vw;
    /* 画面の高さ - (ヘッダー:64px + タイトルバー:48px + パンくずリスト:77px + パディング上下:16px) */
    height: calc(100vh - 205px);
  }
  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-12px);
    }
  }
  ::v-deep(.bounce) {
    animation: bounce 1s ease-in-out infinite;
  }
</style>
